
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Upload, Loader2, Pencil, ShieldAlert, CalendarIcon, Trash2, Wrench } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, differenceInCalendarDays } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FORM = {
  model: "", displacement: "", plate: "", daily_rate: "", deposit_amount: "",
  status: "disponibile", photo_url: "", vehicle_type: "scooter",
  insurance_expiry: null, insurance_company: "", revision_expiry: null,
};

// Helpers for date parsing/formatting
const toDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso.length > 10 ? iso : iso + "T00:00:00"); } catch { return null; }
};
const toISODate = (d) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10) : null);

function ExpiryBadge({ iso, label, isEN }) {
  const d = toDate(iso);
  if (!d) return <span className="text-zinc-600 text-xs">—</span>;
  const days = differenceInCalendarDays(d, new Date());
  let cls = "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30";
  let icon = null;
  if (days < 0) {
    cls = "bg-red-500/20 text-red-400 border border-red-500/40 animate-pulse";
    icon = <ShieldAlert className="w-3 h-3" />;
  } else if (days <= 7) {
    cls = "bg-red-500/15 text-red-400 border border-red-500/30";
    icon = <ShieldAlert className="w-3 h-3" />;
  } else if (days <= 30) {
    cls = "bg-orange-400/15 text-orange-300 border border-orange-400/30";
  } else if (days <= 60) {
    cls = "bg-yellow-400/15 text-yellow-300 border border-yellow-400/30";
  }
  const txt = days < 0
    ? (isEN ? `Expired ${Math.abs(days)}d ago` : `Scaduta da ${Math.abs(days)}g`)
    : (isEN ? `${days}d left` : `tra ${days}g`);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ${cls}`} title={`${label}: ${format(d, "dd/MM/yyyy")}`}>
      {icon}
      <span className="font-mono">{format(d, "dd/MM/yy")}</span>
      <span className="opacity-70">·</span>
      <span>{txt}</span>
    </span>
  );
}

export default function AdminFleet() {
  const { t, i18n } = useTranslation();
  const [scooters, setScooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [maintenanceFor, setMaintenanceFor] = useState(null);
  const [lastMinuteFor, setLastMinuteFor] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const STATUS_LABELS = {
    disponibile: { label: t("admin.status_disponibile"), className: "bg-[#22c55e]/15 text-[#22c55e]" },
    prenotato: { label: t("admin.status_prenotato"), className: "bg-[#FFD700]/15 text-[#FFD700]" },
    noleggiato: { label: i18n.language === "en" ? "Rented" : "Noleggiato", className: "bg-orange-500/20 text-orange-400" },
    sospeso: { label: i18n.language === "en" ? "Off-season" : "Sospeso", className: "bg-blue-500/15 text-blue-300" },
    manutenzione: { label: t("admin.status_manutenzione"), className: "bg-red-500/15 text-red-400" },
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scooters");
      setScooters(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/scooters", { params: { include_hidden: 1 } });
        if (active) { setScooters(data); setLoading(false); }
      } catch { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/scooter-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("photo_url", `${BACKEND_URL}${data.url}`);
      toast.success(t("admin.image_uploaded"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        model: form.model,
        displacement: parseInt(form.displacement, 10),
        plate: form.plate.toUpperCase(),
        daily_rate: parseFloat(form.daily_rate),
        deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : 0,
        status: form.status,
        photo_url: form.photo_url || null,
        vehicle_type: form.vehicle_type,
        insurance_expiry: toISODate(form.insurance_expiry),
        insurance_company: form.insurance_company || null,
        revision_expiry: toISODate(form.revision_expiry),
      };
      if (editingId) {
        await api.patch(`/scooters/${editingId}`, payload);
        toast.success(i18n.language === "en" ? "Vehicle updated" : "Veicolo aggiornato");
      } else {
        await api.post("/scooters", payload);
        toast.success(t("admin.scooter_added"));
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      model: s.model,
      displacement: String(s.displacement),
      plate: s.plate,
      daily_rate: String(s.daily_rate || s.hourly_rate || ""),
      deposit_amount: s.deposit_amount != null ? String(s.deposit_amount) : "",
      status: s.status,
      photo_url: s.photo_url || "",
      vehicle_type: s.vehicle_type || "scooter",
      insurance_expiry: toDate(s.insurance_expiry),
      insurance_company: s.insurance_company || "",
      revision_expiry: toDate(s.revision_expiry),
    });
    setOpen(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/scooters/${id}`, { status });
      toast.success(t("admin.status_updated"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteScooter = async (s) => {
    const msg = i18n.language === "en"
      ? `Permanently delete vehicle "${s.model} – ${s.plate}"? This cannot be undone.`
      : `Eliminare definitivamente il veicolo "${s.model} – ${s.plate}"? L'operazione è irreversibile.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/scooters/${s.id}`);
      toast.success(i18n.language === "en" ? "Vehicle deleted" : "Veicolo eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">{t("admin.fleet_tag")}</p>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("admin.fleet")}</h1>
          <p className="text-zinc-400 mt-1">{t("admin.fleet_total", { n: scooters.length })}</p>
        </div>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY_FORM); } }}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="add-scooter-button">
              <Plus className="w-4 h-4 mr-2" /> {t("admin.add_scooter")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">
                {editingId ? (i18n.language === "en" ? "Edit vehicle" : "Modifica veicolo") : t("admin.new_scooter")}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={onCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{i18n.language === "en" ? "Type" : "Tipo"}</Label>
                  <Select value={form.vehicle_type} onValueChange={(v) => setField("vehicle_type", v)}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <SelectItem value="scooter">Scooter</SelectItem>
                      <SelectItem value="auto">{i18n.language === "en" ? "Car" : "Auto"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("admin.model")}</Label>
                  <Input required value={form.model} onChange={(e) => setField("model", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="scooter-model-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("admin.displacement_cc")}</Label>
                  <Input required type="number" value={form.displacement} onChange={(e) => setField("displacement", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="scooter-displacement-input" />
                </div>
                <div>
                  <Label>{t("admin.plate")}</Label>
                  <Input required value={form.plate} onChange={(e) => setField("plate", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5 uppercase" data-testid="scooter-plate-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{i18n.language === "en" ? "Daily rate €" : "Tariffa €/giorno"}</Label>
                  <Input required type="number" step="0.01" value={form.daily_rate} onChange={(e) => setField("daily_rate", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="scooter-rate-input" />
                </div>
                <div>
                  <Label>{i18n.language === "en" ? "Security deposit €" : "Cauzione €"}</Label>
                  <Input type="number" step="0.01" min="0" placeholder="es. 200" value={form.deposit_amount} onChange={(e) => setField("deposit_amount", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="scooter-deposit-input" />
                  <p className="text-[10px] text-zinc-500 mt-1 leading-tight">
                    {i18n.language === "en"
                      ? "Held at pickup, released at hand-back after damage check."
                      : "Versata al ritiro, restituita alla riconsegna dopo verifica danni."}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("admin.status")}</Label>
                  <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="scooter-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <SelectItem value="disponibile">{t("admin.status_disponibile")}</SelectItem>
                      <SelectItem value="prenotato">{t("admin.status_prenotato")}</SelectItem>
                      <SelectItem value="noleggiato">{i18n.language === "en" ? "Rented" : "Noleggiato"}</SelectItem>
                      <SelectItem value="sospeso">{i18n.language === "en" ? "Off-season (storage)" : "Sospeso (rimessaggio)"}</SelectItem>
                      <SelectItem value="manutenzione">{t("admin.status_manutenzione")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t("admin.image")}</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-zinc-950 border-zinc-800 hover:bg-zinc-800" data-testid="scooter-upload-button">
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    {t("admin.upload_image")}
                  </Button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={onUpload} hidden data-testid="scooter-file-input" />
                  {form.photo_url && <img src={form.photo_url} alt="" className="w-12 h-12 rounded object-cover border border-zinc-800" />}
                </div>
                <Input className="bg-zinc-950 border-zinc-800 mt-2 text-xs" placeholder={t("admin.or_paste_url")} value={form.photo_url} onChange={(e) => setField("photo_url", e.target.value)} data-testid="scooter-photo-url-input" />
              </div>

              {/* Documenti & scadenze */}
              <div className="border-t border-zinc-800 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="w-4 h-4 text-[#FFD700]" />
                  <p className="text-xs uppercase tracking-widest text-zinc-400">
                    {i18n.language === "en" ? "Documents & expiry" : "Documenti e scadenze"}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{i18n.language === "en" ? "Insurance expiry" : "Scadenza assicurazione"}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline"
                                className="bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:text-zinc-100 mt-1.5 w-full justify-start font-normal"
                                data-testid="insurance-expiry-trigger">
                          <CalendarIcon className="w-4 h-4 mr-2 text-[#FFD700]" />
                          {form.insurance_expiry
                            ? format(form.insurance_expiry, "PPP", { locale: i18n.language === "en" ? enUS : itLocale })
                            : (i18n.language === "en" ? "Pick a date" : "Scegli una data")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                        <Calendar mode="single" selected={form.insurance_expiry}
                                  onSelect={(d) => setField("insurance_expiry", d || null)}
                                  locale={i18n.language === "en" ? enUS : itLocale}
                                  initialFocus className="text-zinc-100" />
                        {form.insurance_expiry && (
                          <div className="p-2 border-t border-zinc-800">
                            <Button type="button" variant="ghost" size="sm"
                                    className="w-full text-xs text-zinc-400 hover:text-zinc-100"
                                    onClick={() => setField("insurance_expiry", null)}>
                              {i18n.language === "en" ? "Clear" : "Cancella"}
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>{i18n.language === "en" ? "Insurance company" : "Compagnia assicurativa"}</Label>
                    <Input value={form.insurance_company}
                           onChange={(e) => setField("insurance_company", e.target.value)}
                           placeholder={i18n.language === "en" ? "e.g. Genertel" : "es. Genertel"}
                           className="bg-zinc-950 border-zinc-800 mt-1.5"
                           data-testid="insurance-company-input" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>{i18n.language === "en" ? "Next revision (MOT)" : "Prossima revisione"}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline"
                              className="bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:text-zinc-100 mt-1.5 w-full justify-start font-normal"
                              data-testid="revision-expiry-trigger">
                        <CalendarIcon className="w-4 h-4 mr-2 text-[#FFD700]" />
                        {form.revision_expiry
                          ? format(form.revision_expiry, "PPP", { locale: i18n.language === "en" ? enUS : itLocale })
                          : (i18n.language === "en" ? "Pick a date" : "Scegli una data")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                      <Calendar mode="single" selected={form.revision_expiry}
                                onSelect={(d) => setField("revision_expiry", d || null)}
                                locale={i18n.language === "en" ? enUS : itLocale}
                                initialFocus className="text-zinc-100" />
                      {form.revision_expiry && (
                        <div className="p-2 border-t border-zinc-800">
                          <Button type="button" variant="ghost" size="sm"
                                  className="w-full text-xs text-zinc-400 hover:text-zinc-100"
                                  onClick={() => setField("revision_expiry", null)}>
                            {i18n.language === "en" ? "Clear" : "Cancella"}
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  <p className="text-[11px] text-zinc-500 mt-1.5">
                    {i18n.language === "en"
                      ? "Telegram alerts will fire 60/30/14/7/3/1 days before expiry (and daily if expired)."
                      : "Ricevi notifiche Telegram a 60/30/14/7/3/1 giorni dalla scadenza (e quotidianamente se scaduta)."}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="submit-scooter-button">
                  {submitting ? t("admin.saving") : t("common.save")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-800 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.photo")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.model")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.plate")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.displacement_cc")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">€/{i18n.language === "en" ? "day" : "giorno"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{i18n.language === "en" ? "Insurance" : "Assicurazione"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{i18n.language === "en" ? "Revision" : "Revisione"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.status")}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{t("admin.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-zinc-500 text-center py-8">{t("common.loading")}</TableCell></TableRow>
            ) : scooters.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-zinc-500 text-center py-8">{t("admin.no_scooters")}</TableCell></TableRow>
            ) : scooters.map((s) => {
              const st = STATUS_LABELS[s.status] || STATUS_LABELS.disponibile;
              return (
                <TableRow key={s.id} className="border-zinc-800" data-testid="fleet-row">
                  <TableCell>
                    {s.photo_url ? <img src={s.photo_url} alt="" className="w-12 h-12 rounded object-cover border border-zinc-800" /> : <div className="w-12 h-12 rounded bg-zinc-800" />}
                  </TableCell>
                  <TableCell className="font-medium">{s.model}</TableCell>
                  <TableCell className="font-mono uppercase text-sm">{s.plate}</TableCell>
                  <TableCell className="text-zinc-400">{s.displacement} cc</TableCell>
                  <TableCell className="text-[#FFD700] font-semibold">€{(s.daily_rate || s.hourly_rate || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <ExpiryBadge iso={s.insurance_expiry} label="Assicurazione" isEN={i18n.language === "en"} />
                  </TableCell>
                  <TableCell>
                    <ExpiryBadge iso={s.revision_expiry} label="Revisione" isEN={i18n.language === "en"} />
                  </TableCell>
                  <TableCell><Badge className={`${st.className} border-0`}>{st.label}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="text-zinc-300 hover:text-[#FFD700] hover:bg-zinc-800 h-8 px-2" data-testid="fleet-edit-button">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setMaintenanceFor(s)}
                              className="text-zinc-300 hover:text-[#FFD700] hover:bg-zinc-800 h-8 px-2"
                              title={i18n.language === "en" ? "Maintenance log" : "Scheda manutenzioni"}
                              data-testid="fleet-maintenance-button">
                        <Wrench className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setLastMinuteFor(s)}
                              className={`h-8 px-2 ${s.last_minute_active ? "text-red-400 hover:text-red-300 hover:bg-red-500/10" : "text-zinc-500 hover:text-red-400 hover:bg-red-500/10"}`}
                              title={i18n.language === "en" ? "Toggle Last Minute" : "Last Minute"}
                              data-testid="fleet-last-minute-button">
                        <span className="text-base leading-none">🔥</span>
                      </Button>
                      <Select value={s.status} onValueChange={(v) => changeStatus(s.id, v)}>
                        <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 w-32 text-xs" data-testid="fleet-change-status"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                          <SelectItem value="disponibile">{t("admin.status_disponibile")}</SelectItem>
                          <SelectItem value="prenotato">{t("admin.status_prenotato")}</SelectItem>
                          <SelectItem value="noleggiato">{i18n.language === "en" ? "Rented" : "Noleggiato"}</SelectItem>
                          <SelectItem value="sospeso">{i18n.language === "en" ? "Off-season (storage)" : "Sospeso (rimessaggio)"}</SelectItem>
                          <SelectItem value="manutenzione">{t("admin.status_manutenzione")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => deleteScooter(s)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 px-2"
                              title={i18n.language === "en" ? "Delete vehicle" : "Elimina veicolo"}
                              data-testid="fleet-delete-button">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <MaintenanceDialog
        scooter={maintenanceFor}
        onClose={() => setMaintenanceFor(null)}
        isEN={i18n.language === "en"}
      />

      <LastMinuteDialog
        scooter={lastMinuteFor}
        onClose={() => setLastMinuteFor(null)}
        onSaved={() => { setLastMinuteFor(null); load(); }}
        isEN={i18n.language === "en"}
      />
    </div>
  );
}

// ───────────────────────── Maintenance dialog ─────────────────────────
const MAINTENANCE_LABELS = {
  olio: { it: "Olio", en: "Oil" },
  filtro_olio: { it: "Filtro olio", en: "Oil filter" },
  filtro_aria: { it: "Filtro aria", en: "Air filter" },
  freni: { it: "Freni", en: "Brakes" },
  gomme: { it: "Tyres", en: "Tyres" }, // en already correct
  altro: { it: "Altro", en: "Other" },
};

function MaintenanceDialog({ scooter, onClose, isEN }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("olio");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [km, setKm] = useState("");
  const [cost, setCost] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const open = !!scooter;

  useEffect(() => {
    if (!open) { setData(null); return undefined; }
    let active = true;
    // Defer the actual work into a microtask so the lint rule
    // `react-hooks/set-state-in-effect` doesn't flag the setState calls
    // inside the async fetch (canonical pattern, false-positive otherwise).
    Promise.resolve().then(async () => {
      if (!active) return;
      setLoading(true);
      try {
        const r = await api.get(`/scooters/${scooter.id}/maintenance`);
        if (active) setData(r.data);
      } catch (e) {
        if (active) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (active) setLoading(false);
      }
    });
    return () => { active = false; };
  }, [open, scooter]);

  const add = async () => {
    if (!date) { toast.error(isEN ? "Pick a date" : "Inserisci la data"); return; }
    setSaving(true);
    try {
      await api.post(`/scooters/${scooter.id}/maintenance`, {
        type, date,
        km: km ? Number(km) : null,
        cost: cost ? Number(cost) : null,
        notes: notes.trim() || null,
      });
      toast.success(isEN ? "Service logged" : "Intervento registrato");
      const r = await api.get(`/scooters/${scooter.id}/maintenance`);
      setData(r.data);
      setKm(""); setCost(""); setNotes("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (entryId) => {
    if (!window.confirm(isEN ? "Delete this service entry?" : "Eliminare questo intervento?")) return;
    try {
      await api.delete(`/scooters/${scooter.id}/maintenance/${entryId}`);
      const r = await api.get(`/scooters/${scooter.id}/maintenance`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="maintenance-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#FFD700]" />
            {isEN ? "Maintenance log" : "Scheda manutenzioni"}
            {scooter && <span className="text-zinc-400 text-base font-normal ml-2">{scooter.model} · {scooter.plate}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* Last service by type — quick health snapshot */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {["olio", "filtro_olio", "filtro_aria", "freni", "gomme"].map((k) => {
              const last = data.last_by_type?.[k];
              return (
                <div key={k} className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{MAINTENANCE_LABELS[k][isEN ? "en" : "it"]}</div>
                  {last ? (
                    <div className="text-sm text-zinc-200 font-medium mt-0.5">
                      {last.date}{last.km != null ? <span className="text-zinc-500 ml-1">· {last.km} km</span> : null}
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-600 mt-0.5">{isEN ? "never" : "mai"}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add new entry */}
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 space-y-2">
          <Label className="text-xs uppercase tracking-widest text-[#FFD700]">{isEN ? "New service" : "Nuovo intervento"}</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="maintenance-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                {["olio", "filtro_olio", "filtro_aria", "freni", "gomme", "altro"].map((k) => (
                  <SelectItem key={k} value={k}>{MAINTENANCE_LABELS[k][isEN ? "en" : "it"]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="bg-zinc-950 border-zinc-800" data-testid="maintenance-date-input" />
            <Input type="number" placeholder="km" value={km} onChange={(e) => setKm(e.target.value)}
                   className="bg-zinc-950 border-zinc-800" data-testid="maintenance-km-input" />
            <Input type="number" step="0.01" placeholder={isEN ? "Cost €" : "Costo €"} value={cost} onChange={(e) => setCost(e.target.value)}
                   className="bg-zinc-950 border-zinc-800" data-testid="maintenance-cost-input" />
          </div>
          <Input placeholder={isEN ? "Notes (optional)" : "Note (opzionale)"} value={notes} onChange={(e) => setNotes(e.target.value)}
                 className="bg-zinc-950 border-zinc-800" data-testid="maintenance-notes-input" />
          <Button onClick={add} disabled={saving} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="maintenance-add-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            {isEN ? "Add service" : "Aggiungi intervento"}
          </Button>
        </div>

        {/* History */}
        <div className="border border-zinc-800 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{isEN ? "Date" : "Data"}</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{isEN ? "Type" : "Tipo"}</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">km</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">€</TableHead>
                <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{isEN ? "Notes" : "Note"}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-zinc-500 text-center py-6">{isEN ? "Loading…" : "Caricamento…"}</TableCell></TableRow>
              ) : !data || data.entries.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-zinc-500 text-center py-6">{isEN ? "No service logged yet." : "Nessun intervento registrato."}</TableCell></TableRow>
              ) : data.entries.map((e) => (
                <TableRow key={e.id} className="border-zinc-800" data-testid={`maintenance-row-${e.id}`}>
                  <TableCell className="text-zinc-300 font-mono">{e.date}</TableCell>
                  <TableCell className="text-zinc-200">{MAINTENANCE_LABELS[e.type]?.[isEN ? "en" : "it"] || e.type}</TableCell>
                  <TableCell className="text-zinc-400">{e.km ?? "—"}</TableCell>
                  <TableCell className="text-zinc-400">{e.cost != null ? `€${Number(e.cost).toFixed(2)}` : "—"}</TableCell>
                  <TableCell className="text-zinc-400 text-sm">{e.notes || "—"}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => remove(e.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                            data-testid={`maintenance-delete-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ───────────────────────── Last-Minute dialog ─────────────────────────
function LastMinuteDialog({ scooter, onClose, onSaved, isEN }) {
  const [active, setActive] = useState(false);
  const [pct, setPct] = useState(15);
  const [until, setUntil] = useState("");
  const [saving, setSaving] = useState(false);
  const open = !!scooter;

  useEffect(() => {
    if (!open) return;
    setActive(!!scooter.last_minute_active);
    setPct(scooter.last_minute_discount_pct || 15);
    setUntil(scooter.last_minute_until || "");
  }, [open, scooter]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/scooters/${scooter.id}/last-minute`, {
        active,
        discount_pct: active ? Number(pct) : null,
        until: active && until ? until : null,
      });
      toast.success(isEN ? "Saved" : "Salvato");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="last-minute-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="text-2xl">🔥</span>
            Last Minute
            <span className="text-zinc-400 text-base font-normal ml-1">{scooter.model} · {scooter.plate}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
                   className="w-4 h-4 accent-red-500" data-testid="lm-active-checkbox" />
            <span className="text-sm">{isEN ? "Activate Last Minute on this scooter" : "Attiva Last Minute su questo veicolo"}</span>
          </label>
          {active && (
            <>
              <div>
                <Label className="text-xs text-zinc-400">{isEN ? "Discount %" : "Sconto %"}</Label>
                <Input type="number" min={1} max={50} value={pct} onChange={(e) => setPct(e.target.value)}
                       className="bg-zinc-950 border-zinc-800" data-testid="lm-pct-input" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">
                  {isEN ? "Active until (optional)" : "Attivo fino al (opzionale)"}
                </Label>
                <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
                       className="bg-zinc-950 border-zinc-800" data-testid="lm-until-input" />
                <p className="text-[11px] text-zinc-500 mt-1">
                  {isEN ? "Leave blank for no end date." : "Lascia vuoto per nessuna scadenza."}
                </p>
              </div>
            </>
          )}
          <p className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-800">
            {isEN
              ? "The discount is auto-applied at checkout. It's not stackable with promo codes or long-stay discounts — the largest discount wins."
              : "Lo sconto si applica automaticamente al checkout. Non cumulabile con codici promo o sconto durata — vince il più alto."}
          </p>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="ghost" onClick={onClose} className="text-zinc-400">{isEN ? "Cancel" : "Annulla"}</Button>
            <Button onClick={save} disabled={saving}
                    className={active ? "bg-red-500 hover:bg-red-400 text-white font-semibold" : "bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"}
                    data-testid="lm-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {active ? (isEN ? "Activate" : "Attiva") : (isEN ? "Deactivate" : "Disattiva")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
